import { useEffect, useState } from 'react';

// "Will this text fit in that width?" answered with the page's REAL font, not a guess. A table or card measures the text it is about to show
// (canvas measureText with the font the page actually uses) and, only if it would not fit, shortens it - so an amount is never cut at its
// last digits, whatever font or zoom the person has. Digits are measured as "0": the figures are tabular, every digit as wide as the widest.
// Where there is no canvas (the tests' jsdom) a width estimate is used instead.
const estimate = (text, fontPx, bold) => String(text).length * fontPx * (bold ? 0.63 : 0.58);

let context = null;
let tried = false;
const canvasContext = () => {
  if (tried) return context;
  tried = true;
  try {
    const isJsdom = typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '');
    if (typeof document !== 'undefined' && !isJsdom) context = document.createElement('canvas').getContext('2d');
  } catch (e) {
    context = null;
  }
  return context;
};

export const textWidth = (text, fontPx, bold = false) => {
  const c = canvasContext();
  if (!c) return estimate(text, fontPx, bold);
  try {
    const family = (typeof window !== 'undefined' && window.getComputedStyle(document.body).fontFamily) || 'sans-serif';
    c.font = `${bold ? 700 : 400} ${fontPx}px ${family}`;
    const w = c.measureText(String(text).replace(/\d/g, '0')).width;
    // +6%: tabular figures (every digit as wide as the widest) are a little wider than the canvas measures them, and weights vary by cell
    return w > 0 ? w * 1.06 : estimate(text, fontPx, bold);
  } catch (e) {
    return estimate(text, fontPx, bold);
  }
};

// true when every one of `texts` fits `colPx` (minus `padding`: the cell padding, border and the click-target margin)
export const textFits = (texts, colPx, fontPx, bold = false, padding = 16) =>
  [].concat(texts).every((t) => textWidth(t, fontPx, bold) + padding <= colPx);

// the biggest type size (down to `minPx`) at which `text` fits `boxPx`
export const fitFontSize = (text, boxPx, maxPx, minPx, bold = true) => {
  for (let f = maxPx; f > minPx; f -= 0.5) if (textWidth(text, f, bold) <= boxPx) return f;
  return minPx;
};

// re-renders once when the web fonts have loaded, so a measurement made with the fallback font is made again with the real one
export const useFontsReady = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let off = false;
    try {
      if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) document.fonts.ready.then(() => !off && setTick((t) => t + 1));
    } catch (e) {
      /* no font loading API: nothing to wait for */
    }
    return () => {
      off = true;
    };
  }, []);
  return tick;
};
